<script setup>
import { ref } from 'vue';

const emit = defineEmits(['apply']);

// Configuracao rapida de provedor de email. Ao escolher um preset,
// emite os hosts/portas/SSL pro componente pai.
//
// Lista cobre os provedores mais comuns no Brasil + Gmail/Outlook.
// Sempre que o provedor recomendar 465/SSL, mantemos 587/STARTTLS
// (porta 465 eh bloqueada pelo nosso servidor — anti-spam Hetzner).

const PROVIDERS = [
  {
    key: 'hostverge',
    label: 'Hostverge / Stackmail',
    imap: { address: 'imap.stackmail.com', port: 993, ssl: true },
    smtp: {
      address: 'smtp.stackmail.com',
      port: 587,
      encryption: 'starttls',
      verifyMode: 'none',
      auth: 'login',
    },
  },
  {
    key: 'hostgator',
    label: 'Hostgator',
    imap: {
      address: '',
      port: 993,
      ssl: true,
      addressPattern: 'mail.{dominio}',
    },
    smtp: {
      address: '',
      port: 587,
      encryption: 'starttls',
      verifyMode: 'none',
      auth: 'login',
      addressPattern: 'mail.{dominio}',
    },
  },
  {
    key: 'locaweb',
    label: 'Locaweb',
    imap: { address: 'imap.locaweb.com.br', port: 993, ssl: true },
    smtp: {
      address: 'smtp.locaweb.com.br',
      port: 587,
      encryption: 'starttls',
      verifyMode: 'none',
      auth: 'login',
    },
  },
  {
    key: 'gmail',
    label: 'Gmail / Google Workspace',
    imap: { address: 'imap.gmail.com', port: 993, ssl: true },
    smtp: {
      address: 'smtp.gmail.com',
      port: 587,
      encryption: 'starttls',
      verifyMode: 'none',
      auth: 'login',
    },
    note: 'Gmail/Google Workspace exige "senha de aplicativo" (não a senha normal). Crie em myaccount.google.com → Segurança → Senhas de app.',
  },
  {
    key: 'outlook',
    label: 'Outlook / Microsoft 365',
    imap: { address: 'outlook.office365.com', port: 993, ssl: true },
    smtp: {
      address: 'smtp.office365.com',
      port: 587,
      encryption: 'starttls',
      verifyMode: 'none',
      auth: 'login',
    },
  },
];

const selectedKey = ref('');
const domain = ref('');
const note = ref('');

const onApply = () => {
  const preset = PROVIDERS.find(p => p.key === selectedKey.value);
  if (!preset) return;

  const imap = { ...preset.imap };
  const smtp = { ...preset.smtp };
  // Hostgator (e similares baseados em domínio) precisa do dominio
  // do cliente. Substitui {dominio} pelo valor digitado.
  if (preset.imap.addressPattern && domain.value) {
    imap.address = preset.imap.addressPattern.replace(
      '{dominio}',
      domain.value
    );
  }
  if (preset.smtp.addressPattern && domain.value) {
    smtp.address = preset.smtp.addressPattern.replace(
      '{dominio}',
      domain.value
    );
  }

  note.value = preset.note || '';
  emit('apply', { imap, smtp, providerKey: preset.key });
};

const needsDomain = () => {
  const preset = PROVIDERS.find(p => p.key === selectedKey.value);
  return !!(preset?.imap?.addressPattern || preset?.smtp?.addressPattern);
};

const ui = {
  title: 'Configuração rápida',
  help: 'Escolha seu provedor pra pré-preencher servidor, porta e criptografia.',
  provider: 'Provedor',
  select: '— selecione —',
  yourDomain: 'Seu domínio',
  domainPh: 'ex: empresa.com.br',
  apply: 'Aplicar',
};
</script>

<template>
  <div class="mb-4 p-3 border border-n-weak rounded-lg bg-n-stone-25">
    <div class="flex items-center gap-2 mb-2">
      <span class="i-lucide-zap size-4 text-n-amber-11" />
      <h4 class="text-sm font-semibold text-n-slate-12">{{ ui.title }}</h4>
    </div>
    <p class="text-xs text-n-slate-10 mb-3">
      {{ ui.help }}
    </p>

    <div class="flex items-end gap-2 flex-wrap">
      <label class="text-xs text-n-slate-11 flex-1 min-w-[180px]">
        {{ ui.provider }}
        <select
          v-model="selectedKey"
          class="reset-base w-full mt-1 text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background text-n-slate-12"
        >
          <option value="">{{ ui.select }}</option>
          <option v-for="p in PROVIDERS" :key="p.key" :value="p.key">
            {{ p.label }}
          </option>
        </select>
      </label>

      <label
        v-if="selectedKey && needsDomain()"
        class="text-xs text-n-slate-11 flex-1 min-w-[180px]"
      >
        {{ ui.yourDomain }}
        <input
          v-model="domain"
          type="text"
          :placeholder="ui.domainPh"
          class="reset-base w-full mt-1 text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background text-n-slate-12"
        />
      </label>

      <button
        type="button"
        class="px-3 py-2 text-xs font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50"
        :disabled="!selectedKey || (needsDomain() && !domain)"
        @click="onApply"
      >
        {{ ui.apply }}
      </button>
    </div>

    <p
      v-if="note"
      class="mt-2 text-[11px] text-n-amber-12 bg-n-amber-2 border border-n-amber-6 rounded px-2 py-1.5"
    >
      {{ note }}
    </p>
  </div>
</template>
